import React, { useEffect, useState } from 'react';
import { Image, Spin, Tag } from 'antd';
import { shipmentApi, type TrackingDisplayDTO, type TrackInfoItemDTO, type InputChangeItemDTO, type TrackingDwellSummaryDTO } from '../api/kfc';

const TRUCK_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const WARN = '#fa8c16';
const WARN_TEXT = '#d46b08';

interface TrackingDisplayPanelProps {
  /** 轨迹展示数据（来自 GET /shipments/:id/tracking-display） */
  data: TrackingDisplayDTO | null | undefined;
  /** 无 data 时的兜底展示（原 agentTrackingLatest 格式） */
  fallbackEvents?: { time?: string; location?: string; description?: string }[];
  loading?: boolean;
  shipmentId?: string;
}

const ManualEvidenceImage: React.FC<{ shipmentId: string; fileId: string; fileName: string; eventId?: string }> = ({
  shipmentId,
  fileId,
  fileName,
  eventId,
}) => {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    const req = eventId
      ? shipmentApi.getManualTrackEvidence(shipmentId, eventId, fileId)
      : shipmentApi.getManualDeliveryEvidence(shipmentId, fileId);
    req
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [shipmentId, fileId, eventId]);
  if (!url) {
    return (
      <div style={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
        <Spin size="small" />
      </div>
    );
  }
  return <Image src={url} alt={fileName} width={64} height={64} style={{ objectFit: 'cover', borderRadius: 4 }} />;
};

function formatDwellLine(item: TrackInfoItemDTO): string | null {
  if (item.dwellDays == null || Number.isNaN(Number(item.dwellDays))) return null;
  const days = Number(item.dwellDays);
  const thr =
    item.thresholdDays != null && !Number.isNaN(Number(item.thresholdDays))
      ? `（阈值 ${item.thresholdDays} 天）`
      : '';
  if (item.ongoing) {
    return `进行中 · 已停留 ${days} 天${thr}`;
  }
  return `${days} 天${thr}`;
}

/** 轨迹时间线单条：进入/离开/停留 + 超阈值样式（016 §11） */
function TrackTimelineItem({
  item,
  isLatest,
  isLast,
  shipmentId,
}: {
  item: TrackInfoItemDTO;
  isLatest: boolean;
  isLast: boolean;
  shipmentId?: string;
}) {
  const over = !!item.overThreshold;
  const time = item.trackTime ?? '';
  const status = item.trackStatusText ?? '';
  const location = [item.trackStation, item.trackStationEn].filter(Boolean).join(' ') || '-';
  const desc = item.trackDesc ?? item.trackDescEn ?? '';
  const entered = item.enteredAt ?? time;
  const leftLabel = item.ongoing ? '进行中' : item.leftAt || '-';
  const dwellLine = formatDwellLine(item);
  const dotColor = over ? WARN : isLatest ? '#1890ff' : '#d9d9d9';
  const lineColor = over ? WARN : '#e8e8e8';
  const evidence = shipmentId && item.evidenceFiles && item.evidenceFiles.length > 0 && (
    <Image.PreviewGroup>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {item.evidenceFiles.map((f) => (
          <ManualEvidenceImage
            key={`${f.eventId || 'delivery'}-${f.id}`}
            shipmentId={shipmentId}
            fileId={f.id}
            eventId={f.eventId}
            fileName={f.name || f.id}
          />
        ))}
      </div>
    </Image.PreviewGroup>
  );

  if (item.documentLocation) {
    return (
      <div style={{ display: 'flex', gap: 12, marginBottom: isLast ? 0 : 16 }}>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: isLatest ? '#1890ff' : '#d9d9d9',
              flexShrink: 0,
            }}
          />
          {!isLast && <div style={{ width: 2, flex: 1, minHeight: 24, background: '#e8e8e8', marginTop: 4 }} />}
        </div>
        <div style={{ flex: 1, paddingBottom: 4 }}>
          <div style={{ fontWeight: 500 }}>{status || '清关单证'}</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{time || '-'}</div>
          {desc && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{desc}</div>}
          {evidence}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: isLast ? 0 : 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
          }}
        />
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 24, background: lineColor, marginTop: 4 }} />}
      </div>
      <div style={{ flex: 1, paddingBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <div style={{ fontWeight: 500 }}>
            {status && <span style={{ marginRight: 8 }}>{status}</span>}
            {location && <span style={{ color: '#666' }}>{location}</span>}
          </div>
          {item.source === 'MANUAL' && (
            <Tag color="green" style={{ margin: 0 }}>
              人工
            </Tag>
          )}
          {item.source === 'VESSEL' && (
            <Tag color="blue" style={{ margin: 0 }}>
              船位
            </Tag>
          )}
          {over && (
            <Tag color="orange" style={{ margin: 0 }}>
              超时
            </Tag>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#666', lineHeight: 1.7 }}>
          <div>进入　{entered || '-'}</div>
          <div>离开　{leftLabel}</div>
          {dwellLine != null && (
            <div style={{ color: over ? WARN_TEXT : '#666', fontWeight: over ? 600 : 400 }}>
              停留　{dwellLine}
            </div>
          )}
        </div>
        {desc && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{desc}</div>}
        {evidence}
      </div>
    </div>
  );
}

/** 入库信息时间线单条 */
function InputChangeTimelineItem({
  item,
  isLatest,
  isLast,
}: {
  item: InputChangeItemDTO;
  isLatest: boolean;
  isLast: boolean;
}) {
  const time = item.createTime ?? '';
  const busiCode = item.busiCode ?? '';

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: isLast ? 0 : 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: isLatest ? '#1890ff' : '#d9d9d9',
            flexShrink: 0,
          }}
        />
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 24, background: '#e8e8e8', marginTop: 4 }} />}
      </div>
      <div style={{ flex: 1, paddingBottom: 4 }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>{time}</div>
        <div style={{ fontWeight: 500 }}>{busiCode}</div>
      </div>
    </div>
  );
}

function DwellSummaryBar({ summary }: { summary: TrackingDwellSummaryDTO }) {
  const n = summary.segmentCount ?? 0;
  if (n <= 0) return null;
  const over = summary.overCount ?? 0;
  const max = summary.maxDwellDays;
  const label = summary.maxDwellLabel;
  return (
    <div
      style={{
        marginBottom: 16,
        padding: '8px 12px',
        background: over > 0 ? '#fff7e6' : '#fafafa',
        border: `1px solid ${over > 0 ? '#ffd591' : '#f0f0f0'}`,
        borderRadius: 4,
        color: '#595959',
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      共 {n} 段
      <span style={{ margin: '0 8px', color: '#d9d9d9' }}>|</span>
      超时{' '}
      <span style={{ color: over > 0 ? WARN_TEXT : undefined, fontWeight: over > 0 ? 600 : 400 }}>{over}</span> 段
      {max != null && (
        <>
          <span style={{ margin: '0 8px', color: '#d9d9d9' }}>|</span>
          最长停留：{max} 天{label ? ` @ ${label}` : ''}
        </>
      )}
    </div>
  );
}

const TrackingDisplayPanel: React.FC<TrackingDisplayPanelProps> = ({
  data,
  fallbackEvents,
  loading,
  shipmentId,
}) => {
  if (loading) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
        加载轨迹中…
      </div>
    );
  }

  const hasRich = data && (data.trackInfoList?.length || data.inputChanges?.length || data.serviceName != null || data.latestTime != null);
  const hasFallback = fallbackEvents && fallbackEvents.length > 0;

  if (!hasRich && !hasFallback) {
    return (
      <div style={{ color: '#999', fontSize: 12 }}>
        暂无轨迹，点击「同步轨迹数据」从代理拉取
      </div>
    );
  }

  // 富展示：头部 + 摘要 + 转运信息 + 入库信息
  if (hasRich && data) {
    const trackList = data.trackInfoList ?? [];
    const inputChanges = data.inputChanges ?? [];

    return (
      <div style={{ fontSize: 12 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#666' }}>{TRUCK_ICON}</span>
            <span>
              <span style={{ fontWeight: 500 }}>{data.userNo ?? '-'}/</span>
              <span style={{ color: '#666', marginLeft: 4 }}>{data.latestStatus ?? '-'}</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 24, color: '#666' }}>
            <div>
              <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>渠道</div>
              <div>{data.serviceName ?? '-'}</div>
            </div>
            <div>
              <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>时间</div>
              <div>{data.latestTime ?? '-'}</div>
            </div>
            <div style={{ maxWidth: 200 }}>
              <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>描述</div>
              <div style={{ wordBreak: 'break-word' }}>{data.latestDetail ?? '-'}</div>
            </div>
          </div>
        </div>

        {data.summary && <DwellSummaryBar summary={data.summary} />}

        {trackList.length > 0 && (
          <>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>转运信息</h4>
            <div style={{ paddingLeft: 4 }}>
              {trackList.map((item, index) => (
                <TrackTimelineItem
                  key={index}
                  item={item}
                  isLatest={index === 0}
                  isLast={index === trackList.length - 1}
                  shipmentId={shipmentId}
                />
              ))}
            </div>
          </>
        )}

        {inputChanges.length > 0 && (
          <>
            <h4 style={{ margin: '20px 0 12px', fontSize: 13, fontWeight: 600 }}>入库信息</h4>
            <div style={{ paddingLeft: 4 }}>
              {inputChanges.map((item, index) => (
                <InputChangeTimelineItem
                  key={index}
                  item={item}
                  isLatest={index === 0}
                  isLast={index === inputChanges.length - 1}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // 兜底：简单时间线
  const events = fallbackEvents ?? [];
  return (
    <div style={{ paddingLeft: 4 }}>
      {events.map((ev, index) => (
        <div key={index} style={{ display: 'flex', gap: 12, marginBottom: index === events.length - 1 ? 0 : 16 }}>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: index === 0 ? '#1890ff' : '#d9d9d9',
              }}
            />
            {index < events.length - 1 && (
              <div style={{ width: 2, flex: 1, minHeight: 24, background: '#e8e8e8', marginTop: 4 }} />
            )}
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>{ev.time || '-'}</div>
            <div style={{ fontWeight: 500 }}>{ev.location || '-'}</div>
            {ev.description && <div style={{ fontSize: 12, color: '#666' }}>{ev.description}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TrackingDisplayPanel;
